import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PageContainer from '../components/Layout/PageContainer';
import {
  getLectureList,
  getStatusByLectureId,
  getStatusByRequestId,
} from '../api/sttApi';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import '../styles/lectureStatus.css';
import '../styles/lectureUpload.css';

const normalizeStatus = (status) => {
  if (!status) return 'unknown';
  const value = status.toLowerCase();
  if (['pending', 'stt_pending', 'uploaded'].includes(value)) return 'pending';
  if (['processing', 'running'].includes(value)) return 'processing';
  if (['completed', 'done'].includes(value)) return 'completed';
  if (['failed', 'error'].includes(value)) return 'failed';
  return 'unknown';
};

const renderStatusBadge = (status, t) => {
  const normalized = normalizeStatus(status);
  const labelMap = {
    pending: t('status_pending'),
    processing: t('status_processing'),
    completed: t('status_completed'),
    failed: t('status_failed'),
    unknown: t('status_unknown'),
  };
  return (
    <span className={`status-badge status-${normalized}`}>
      {labelMap[normalized] || labelMap.unknown}
    </span>
  );
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const shortenLectureId = (lectureId) => {
  if (!lectureId) return '-';
  return lectureId.length > 10 ? `${lectureId.slice(0, 8)}…` : lectureId;
};

const SummarySection = ({ title, list }) => {
  if (!list || list.length === 0) return null;
  return (
    <div className="summary-section">
      <h3>{title}</h3>
      <ul className="summary-list">
        {list.map((item, index) => (
          <li key={`${title}-${index}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const LectureStatusPage = () => {
  const {
    user,
    loading,
    signInWithGoogle,
    authError,
  } = useAuth();
  const { t } = useLanguage();
  const { contextId: contextIdFromPath } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestId = searchParams.get('request_id') || '';
  const queryContextId = searchParams.get('context_id') || '';
  const contextId = contextIdFromPath || queryContextId;
  const initialLectureIdFromUrl = contextId || '';

  const [lectureIdInput, setLectureIdInput] = useState(initialLectureIdFromUrl);
  const [selectedLectureStatus, setSelectedLectureStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [technicalError, setTechnicalError] = useState('');
  const [showTechDetails, setShowTechDetails] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [lastQuery, setLastQuery] = useState({ requestId: '', lectureId: '' });
  const [lectureList, setLectureList] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [selectedLectureId, setSelectedLectureId] = useState(initialLectureIdFromUrl);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [downloadTechnicalError, setDownloadTechnicalError] = useState('');

  const summarySections = useMemo(() => {
    if (!selectedLectureStatus?.summary) return [];
    return [
      { key: 'tldr', label: t('summaryTldr'), list: selectedLectureStatus.summary.tldr },
      { key: 'key_points', label: t('summaryKeyPoints'), list: selectedLectureStatus.summary.key_points },
      { key: 'examples', label: t('summaryExamples'), list: selectedLectureStatus.summary.examples },
      { key: 'homework', label: t('summaryHomework'), list: selectedLectureStatus.summary.homework },
    ];
  }, [selectedLectureStatus, t]);

  const fetchStatus = useCallback(async ({ requestId: req, lectureId: lec }) => {
    if (!req && !lec) {
      setStatusError(t('statusEnterId'));
      return;
    }

    setStatusError('');
    setTechnicalError('');
    setShowTechDetails(false);
    setFetching(true);
    try {
      const payload = req ? await getStatusByRequestId(req) : await getStatusByLectureId(lec);
      const resolvedLectureId = payload?.context_id || payload?.lectureId || lec || '';
      setSelectedLectureStatus(payload);
      setLastQuery({ requestId: req || '', lectureId: resolvedLectureId });
      if (resolvedLectureId) {
        setLectureIdInput(resolvedLectureId);
        setSelectedLectureId(resolvedLectureId);
      }
      setLectureList((prev) => prev.map((item) => {
        const itemId = item.lectureId || item.contextId || item.context_id;
        if (!itemId || itemId !== resolvedLectureId) return item;
        return { ...item, status: payload?.status };
      }));
      if (normalizeStatus(payload?.status) === 'failed') {
        setStatusError(t('status_friendly_error'));
        setTechnicalError(payload?.errorMessage || payload?.error_message || '');
      }
    } catch (error) {
      setSelectedLectureStatus(null);
      setStatusError(t('status_friendly_error'));
      setTechnicalError(error.message || '');
    } finally {
      setFetching(false);
    }
  }, [t]);

  useEffect(() => {
    if (!user) return;
    if (requestId) {
      setSelectedLectureId('');
      fetchStatus({ requestId, lectureId: '' });
    } else if (contextId) {
      setSelectedLectureId(contextId);
      setLectureIdInput(contextId);
      fetchStatus({ requestId: '', lectureId: contextId });
    }
  }, [user, requestId, contextId, fetchStatus]);

  useEffect(() => {
    if (!user) {
      setSelectedLectureStatus(null);
      setLastQuery({ requestId: '', lectureId: '' });
      setLectureList([]);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchList = async () => {
      setIsListLoading(true);
      setListError('');
      try {
        const data = await getLectureList(10);
        setLectureList(Array.isArray(data?.lectures) ? data.lectures : []);
      } catch (err) {
        console.error('Failed to load lecture list', err);
        setListError(t('lecture_history_error'));
      } finally {
        setIsListLoading(false);
      }
    };

    fetchList();
  }, [user, t]);

  const handleSelectLecture = (lectureId) => {
    if (!lectureId) return;
    setSelectedLectureId(lectureId);
    setLectureIdInput(lectureId);
    const params = new URLSearchParams();
    params.set('context_id', lectureId);
    setSearchParams(params);
    fetchStatus({ requestId: '', lectureId });
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = lectureIdInput.trim();
    if (!trimmed) {
      setStatusError(t('statusEnterLectureId'));
      return;
    }
    handleSelectLecture(trimmed);
  };

  const handleRefresh = () => {
    if (!lastQuery.requestId && !lastQuery.lectureId) return;
    fetchStatus(lastQuery);
  };

  const handleCopyLectureId = async (lectureId) => {
    if (!lectureId) return;
    try {
      await navigator.clipboard.writeText(lectureId);
    } catch (err) {
      console.error('Failed to copy lecture ID', err);
    }
  };

  const handleDownloadTranscript = () => {
    setDownloadError('');
    setDownloadTechnicalError('');
    setShowTechDetails(false);

    const lecture = selectedLectureStatus;
    const transcriptUrl = lecture?.transcriptUrl || lecture?.transcript_url;

    if (!lecture || !transcriptUrl) {
      setDownloadError(t('download_no_url'));
      return;
    }

    setDownloading(true);
    try {
      // Open SAS URL directly (no CORS issues)
      window.open(transcriptUrl, '_blank');
    } catch (err) {
      console.error('Transcript download failed', err);
      setDownloadError(t('download_error_generic'));
      setDownloadTechnicalError(err.message || '');
    } finally {
      setDownloading(false);
    }
  };

  const showErrorBox = statusError || downloadError;
  const technicalDetails = technicalError || downloadTechnicalError;

  if (loading) {
    return (
      <PageContainer>
        <div className="card status-empty">{t('loadingSession')}</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="card status-card">
        <div className="status-header">
          <div>
            <h1 className="card-title">{t('statusPageTitle')}</h1>
            <p className="status-helper-text">
              {t('statusSubtitle')}
            </p>
          </div>
          {selectedLectureStatus?.status && renderStatusBadge(selectedLectureStatus.status, t)}
        </div>

        {!user && (
          <div className="status-guest">
            <p>{t('statusPleaseSignIn')}</p>
            <button type="button" className="primary-button" onClick={signInWithGoogle}>
              {t('signInWithGoogle')}
            </button>
            {authError && <div className="error-text">{authError}</div>}
          </div>
        )}

        <div className="lecture-list-section">
          <div className="section-heading">
            <h2>{t('lecture_history_title')}</h2>
            <p className="status-helper-text">{t('lecture_history_hint')}</p>
          </div>
          {!user && (
            <div className="status-empty">{t('lecture_history_signin')}</div>
          )}
          {user && (
            <>
              {isListLoading && (
                <div className="status-empty">{t('lecture_history_loading')}</div>
              )}
              {listError && (
                <div className="status-error inline-error">{listError}</div>
              )}
              {!isListLoading && !listError && lectureList.length === 0 && (
                <div className="status-empty">{t('lecture_history_empty')}</div>
              )}
              {!isListLoading && lectureList.length > 0 && (
                <div className="lecture-list-table-wrapper">
                  <table className="lecture-list-table">
                    <thead>
                      <tr>
                        <th>{t('lecture_id_label')}</th>
                        <th>{t('lecture_title_label')}</th>
                        <th>{t('lecture_status_label')}</th>
                        <th>{t('lecture_updated_label')}</th>
                        <th>{t('lecture_action_label')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lectureList.map((lecture, index) => {
                        const lectureId = lecture.lectureId || lecture.contextId || lecture.context_id;
                        const normalizedStatus = renderStatusBadge(lecture.status, t);
                        return (
                          <tr
                            key={lectureId || `lecture-${index}`}
                            className={lectureId === selectedLectureId ? 'selected' : ''}
                          >
                            <td className="lecture-id-cell">
                              <div className="id-and-copy">
                                <span>{shortenLectureId(lectureId)}</span>
                                <button
                                  type="button"
                                  className="ghost-button copy-ghost"
                                  onClick={() => handleCopyLectureId(lectureId)}
                                  disabled={!lectureId}
                                >
                                  {t('lecture_copy_button')}
                                </button>
                              </div>
                            </td>
                            <td>{lecture.title || t('notAvailable')}</td>
                            <td>{normalizedStatus}</td>
                            <td>{formatDateTime(lecture.updatedAt || lecture.createdAt)}</td>
                            <td>
                              <button
                                type="button"
                                className="ghost-button"
                                onClick={() => handleSelectLecture(lectureId)}
                                disabled={!lectureId}
                              >
                                {t('lecture_view_button')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {user && (
          <>
            <div className="status-search">
              <div className="advanced-label">{t('lecture_advanced_label')}</div>
              <form onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  placeholder={t('statusInputPlaceholder')}
                  value={lectureIdInput}
                  onChange={(event) => setLectureIdInput(event.target.value)}
                />
                <button type="submit" className="secondary-button" disabled={fetching}>
                  {fetching ? t('statusChecking') : t('lecture_check_status_button')}
                </button>
              </form>
              {statusError && !fetching && (
                <div className="status-error">{statusError}</div>
              )}
            </div>

            {!selectedLectureStatus && !fetching && !lectureIdInput && (
              <div className="status-empty">
                {t('statusEmptyState')}
              </div>
            )}

            {selectedLectureStatus && (
              <>
                <div className="status-section meta-grid">
                  <div>
                    <div className="meta-label">{t('metaLectureTitle')}</div>
                    <div>{selectedLectureStatus?.lecture?.title || t('notAvailable')}</div>
                  </div>
                  <div>
                    <div className="meta-label">{t('metaSubject')}</div>
                    <div>{selectedLectureStatus?.lecture?.subject || t('notAvailable')}</div>
                  </div>
                  <div>
                    <div className="meta-label">{t('lecture_tags_label')}</div>
                    <div>{selectedLectureStatus?.lecture?.tags || t('notAvailable')}</div>
                  </div>
                  <div>
                    <div className="meta-label">{t('lecture_language_label')}</div>
                    <div>{selectedLectureStatus?.locale || t('notAvailable')}</div>
                  </div>
                </div>

                <div className="status-section status-actions">
                  <button
                    type="button"
                    className="primary-button"
                    onClick={handleRefresh}
                    disabled={fetching}
                  >
                    {fetching ? t('statusRefreshing') : t('lecture_refresh_button')}
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={handleDownloadTranscript}
                    disabled={downloading || !selectedLectureStatus || normalizeStatus(selectedLectureStatus?.status) !== 'completed' || !(selectedLectureStatus?.transcriptUrl || selectedLectureStatus?.transcript_url)}
                  >
                    {downloading ? t('statusProcessing') : t('download_transcript_button')}
                  </button>
                </div>

                {showErrorBox && (
                  <div className="status-error">
                    {statusError || downloadError}
                    {technicalDetails && (
                      <div className="technical-toggle">
                        <button
                          type="button"
                          className="ghost-button"
                          onClick={() => setShowTechDetails((prev) => !prev)}
                        >
                          {showTechDetails ? t('status_hide_details') : t('status_show_details')}
                        </button>
                        {showTechDetails && (
                          <pre className="technical-details">{technicalDetails}</pre>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {selectedLectureStatus?.summary && normalizeStatus(selectedLectureStatus.status) === 'completed' && (
                  <div className="status-section summary-section">
                    <h2>{t('lecture_ai_summary_title')}</h2>
                    {summarySections.map((section) => (
                      <SummarySection
                        key={section.key}
                        title={section.label}
                        list={section.list}
                      />
                    ))}
                  </div>
                )}

                {normalizeStatus(selectedLectureStatus?.status) === 'processing' && (
                  <div className="info-notice">
                    {t('statusProcessingNote')}
                  </div>
                )}

                {normalizeStatus(selectedLectureStatus?.status) === 'failed' && !statusError && (
                  <div className="status-error">
                    {t('status_friendly_error')}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default LectureStatusPage;
